import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Bot, Send, Loader2, Crosshair, X, CalendarClock, Plus, Trash2, Brain, User } from 'lucide-react';
import Button from '../ui/Button';
import { Card } from '../ui/Card';
import { ActionRow, RichText, SectionTitle, TraceView, useFmt } from './tmShared';
import trafficManagerService, { TrafficManager, TrafficRun, SnapshotCampaign } from '../../services/trafficManagerService';

const inputCls = 'w-full px-3 py-2.5 text-base sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

/** Seletor de campanhas em chips (foco de um pedido ou de uma diretriz). */
const CampaignPicker: React.FC<{ campaigns: SnapshotCampaign[]; value: string[]; onChange: (v: string[]) => void; label: string }> = ({ campaigns, value, onChange, label }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const nome = (id: string) => (campaigns.find((c) => c.id === id) || { nome: id }).nome;
  return (
    <div className="relative">
      <div className="flex flex-wrap items-center gap-1.5">
        <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
          className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full border ${value.length ? 'border-primary-500 text-primary-700 bg-primary-50 dark:bg-primary-900/20 dark:text-primary-300' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'}`}>
          <Crosshair className="w-3.5 h-3.5" />{label}
        </button>
        {value.map((id) => (
          <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 max-w-[14rem]">
            <span className="truncate">{nome(id)}</span>
            <button type="button" onClick={() => onChange(value.filter((x) => x !== id))} aria-label={t('trafficManager.chat.removeFocus', 'Tirar do foco') as string}><X className="w-3 h-3" /></button>
          </span>
        ))}
      </div>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute z-40 mt-1 w-80 max-h-64 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl py-1">
            {campaigns.map((c) => {
              const on = value.includes(c.id);
              return (
                <label key={c.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                  <input type="checkbox" checked={on} className="rounded text-primary-600" onChange={() => onChange(on ? value.filter((x) => x !== c.id) : [...value, c.id])} />
                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${c.status === 'active' ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                  <span className="truncate text-gray-800 dark:text-gray-200">{c.nome}</span>
                </label>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

const ChatTab: React.FC<{
  manager: TrafficManager;
  onDecide: (runId: string, actionId: string | undefined, d: 'approve' | 'reject') => Promise<void>;
  onManagerChange: (m: TrafficManager) => void;
}> = ({ manager, onDecide, onManagerChange }) => {
  const { t, i18n } = useTranslation();
  const f = useFmt();
  const cur = manager.currency || 'BRL';
  const campaigns = manager.snapshot?.campanhas || [];
  const [msgs, setMsgs] = useState<TrafficRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [focus, setFocus] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const [traces, setTraces] = useState<Record<string, TrafficRun | 'loading'>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Diretrizes temporárias
  const [dText, setDText] = useState('');
  const [dUntil, setDUntil] = useState('');
  const [dCamps, setDCamps] = useState<string[]>([]);
  const [savingD, setSavingD] = useState(false);

  const load = useCallback(async () => {
    try {
      const list = await trafficManagerService.runs(manager.id, 30, 'chat');
      setMsgs([...list].reverse());
    } catch { /* silencioso: a tela mostra o que já tem */ } finally { setLoading(false); }
  }, [manager.id]);

  useEffect(() => { load(); }, [load]);
  const pending = msgs.some((m) => m.status === 'running');
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (pending) timer.current = setTimeout(load, 3000);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [pending, msgs, load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [msgs.length, pending]);

  const send = async (message?: string) => {
    const body = (message ?? text).trim();
    if (!body) return;
    setSending(true);
    try {
      const run = await trafficManagerService.chat(manager.id, body, focus, i18n.language);
      setMsgs((m) => [...m, run]);
      setText('');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('trafficManager.chat.sendError', 'Não foi possível enviar'));
    } finally { setSending(false); }
  };

  const toggleTrace = async (runId: string) => {
    if (traces[runId]) { setTraces((p) => { const n = { ...p }; delete n[runId]; return n; }); return; }
    setTraces((p) => ({ ...p, [runId]: 'loading' }));
    try { const full = await trafficManagerService.getRun(runId); setTraces((p) => ({ ...p, [runId]: full })); } catch { setTraces((p) => { const n = { ...p }; delete n[runId]; return n; }); }
  };

  const decide = async (runId: string, actionId: string | undefined, d: 'approve' | 'reject') => {
    setBusy(runId);
    try { await onDecide(runId, actionId, d); await load(); } finally { setBusy(null); }
  };

  const addDirective = async () => {
    if (!dText.trim()) return;
    setSavingD(true);
    try {
      const m = await trafficManagerService.addDirective(manager.id, { text: dText.trim(), campaign_ids: dCamps, until: dUntil || null });
      onManagerChange(m);
      setDText(''); setDUntil(''); setDCamps([]);
      toast.success(t('trafficManager.directives.saved', 'Diretriz salva. Ela entra nas próximas rodadas.'));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('trafficManager.directives.error', 'Não foi possível salvar a diretriz'));
    } finally { setSavingD(false); }
  };

  const removeDirective = async (id: string) => {
    try { onManagerChange(await trafficManagerService.removeDirective(manager.id, id)); } catch { toast.error(t('trafficManager.directives.error', 'Não foi possível salvar a diretriz')); }
  };

  const suggestions = [
    t('trafficManager.chat.s1', 'Como estão as campanhas hoje? Onde estou desperdiçando dinheiro?'),
    t('trafficManager.chat.s2', 'Qual campanha merece mais verba esta semana, e por quê?'),
    t('trafficManager.chat.s3', 'Analise as palavras-chave e os termos de busca e corte o que gasta sem converter.'),
    t('trafficManager.chat.s4', 'Crie automações de proteção para não estourar o custo por conversão.')
  ];
  const hoje = new Date().toISOString().slice(0, 10);
  const directives = (manager.directives || []);

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6">
      <Card className="xl:col-span-2 flex flex-col" padding={false}>
        <div className="p-4 sm:p-6 pb-3 border-b border-gray-100 dark:border-gray-700">
          <SectionTitle icon={Bot}>{t('trafficManager.chat.title', 'Fale com o seu gestor de tráfego')}</SectionTitle>
          <p className="text-sm text-gray-500 -mt-2">{t('trafficManager.chat.help', 'Pergunte ou peça como faria a um gestor de verdade. Com foco, ele só mexe nas campanhas escolhidas. As travas de verba, maturação e anti-vaivém valem aqui também.')}</p>
        </div>

        <div className="flex-1 min-h-[320px] max-h-[640px] overflow-y-auto p-4 sm:p-6 space-y-5">
          {loading && <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>}
          {!loading && msgs.length === 0 && (
            <div className="text-center py-8">
              <Bot className="w-10 h-10 mx-auto text-primary-600 mb-3" />
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">{t('trafficManager.chat.empty', 'Comece com uma pergunta. Ele responde com os números da sua conta.')}</p>
              <div className="flex flex-col gap-2 max-w-md mx-auto">
                {suggestions.map((s) => (
                  <button key={s} type="button" onClick={() => send(s)} disabled={sending}
                    className="text-left text-sm px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-primary-500 text-gray-700 dark:text-gray-200">{s}</button>
                ))}
              </div>
            </div>
          )}
          {msgs.map((m) => {
            const trace = traces[m.id];
            return (
              <div key={m.id} className="space-y-2">
                <div className="flex justify-end gap-2">
                  <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-primary-600 text-white px-4 py-2.5 text-sm whitespace-pre-line">
                    {m.request}
                    {m.focus_campaign_ids && m.focus_campaign_ids.length > 0 && (
                      <div className="mt-1.5 text-[11px] text-white/80 flex items-center gap-1"><Crosshair className="w-3 h-3" />
                        {m.focus_campaign_ids.map((id) => (campaigns.find((c) => c.id === id) || { nome: id }).nome).join(' · ')}
                      </div>
                    )}
                  </div>
                  <User className="w-6 h-6 text-gray-400 shrink-0 mt-1" />
                </div>
                <div className="flex gap-2">
                  <Bot className="w-6 h-6 text-primary-600 shrink-0 mt-1" />
                  <div className="max-w-[90%] flex-1 rounded-2xl rounded-tl-sm bg-gray-50 dark:bg-gray-800 px-4 py-3">
                    {m.status === 'running' && <span className="text-sm text-gray-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />{t('trafficManager.chat.thinking', 'Analisando a conta…')}</span>}
                    {m.status !== 'running' && <RichText text={m.summary || m.error} className="text-sm text-gray-800 dark:text-gray-100" />}
                    {(m.actions || []).length > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
                        {m.actions.map((a) => (
                          <ActionRow key={a.id} a={a} cur={cur} busy={busy === m.id} onApprove={() => decide(m.id, a.id, 'approve')} onReject={() => decide(m.id, a.id, 'reject')} />
                        ))}
                      </div>
                    )}
                    {m.status !== 'running' && (
                      <div className="mt-2 flex items-center gap-3 text-xs text-gray-500">
                        <span>{f.dateTime(m.created_at)}</span>
                        <button type="button" onClick={() => toggleTrace(m.id)} className="inline-flex items-center gap-1 text-primary-600 hover:underline"><Brain className="w-3.5 h-3.5" />{trace ? t('trafficManager.chat.hideReasoning', 'Esconder raciocínio') : t('trafficManager.chat.showReasoning', 'Ver raciocínio')}</button>
                      </div>
                    )}
                    {trace === 'loading' && <Loader2 className="w-4 h-4 animate-spin text-gray-400 mt-2" />}
                    {trace && trace !== 'loading' && <div className="mt-3"><TraceView steps={trace.trace || []} /></div>}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>

        <div className="p-4 sm:p-6 pt-3 border-t border-gray-100 dark:border-gray-700 space-y-2">
          <CampaignPicker campaigns={campaigns} value={focus} onChange={setFocus} label={focus.length ? t('trafficManager.chat.focusOn', 'Foco: só estas') : t('trafficManager.chat.focus', 'Focar em campanhas (opcional)')} />
          <div className="flex gap-2 items-end">
            <textarea
              className={`${inputCls} min-h-[48px] max-h-40 resize-y`}
              rows={2}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={t('trafficManager.chat.placeholder', 'Ex.: hoje quero foco total em leads na campanha de Outsourcing, sem mexer nas outras.') as string}
              aria-label={t('trafficManager.chat.title', 'Fale com o seu gestor de tráfego') as string}
              maxLength={4000}
            />
            <Button onClick={() => send()} disabled={sending || pending || !text.trim()} className="h-12" aria-label={t('trafficManager.chat.send', 'Enviar') as string}>
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </Card>

      <Card>
        <SectionTitle icon={CalendarClock}>{t('trafficManager.directives.title', 'Diretrizes temporárias')}</SectionTitle>
        <p className="text-sm text-gray-500 -mt-2 mb-3">{t('trafficManager.directives.help', 'Ordens com prazo, que entram em toda rodada até a data de fim. Ex.: "até sexta, priorize a campanha de Natal".')}</p>
        <div className="space-y-2">
          <textarea className={`${inputCls} min-h-[72px]`} value={dText} onChange={(e) => setDText(e.target.value)} maxLength={1500}
            placeholder={t('trafficManager.directives.placeholder', 'O que ele deve fazer') as string} aria-label={t('trafficManager.directives.placeholder', 'O que ele deve fazer') as string} />
          <CampaignPicker campaigns={campaigns} value={dCamps} onChange={setDCamps} label={t('trafficManager.directives.campaigns', 'Campanhas (opcional)')} />
          <div className="flex gap-2 items-center">
            <label className="text-xs text-gray-500 shrink-0" htmlFor="tm-d-until">{t('trafficManager.directives.until', 'Vale até')}</label>
            <input id="tm-d-until" type="date" min={hoje} className={inputCls} value={dUntil} onChange={(e) => setDUntil(e.target.value)} />
            <Button size="sm" onClick={addDirective} disabled={savingD || !dText.trim()} isLoading={savingD} leftIcon={<Plus className="w-4 h-4" />}>{t('trafficManager.directives.add', 'Adicionar')}</Button>
          </div>
        </div>
        <div className="mt-4 space-y-2">
          {directives.length === 0 && <p className="text-xs text-gray-400">{t('trafficManager.directives.none', 'Nenhuma diretriz temporária.')}</p>}
          {directives.map((d) => {
            const expirada = !!d.until && d.until < hoje;
            return (
              <div key={d.id} className={`rounded-lg border p-3 text-sm ${expirada ? 'border-gray-200 dark:border-gray-700 opacity-60' : 'border-primary-200 dark:border-primary-800 bg-primary-50/50 dark:bg-primary-900/10'}`}>
                <div className="flex gap-2">
                  <p className="flex-1 text-gray-800 dark:text-gray-200 whitespace-pre-line">{d.text}</p>
                  <button type="button" onClick={() => removeDirective(d.id)} className="text-gray-400 hover:text-red-500 shrink-0" aria-label={t('trafficManager.directives.remove', 'Remover diretriz') as string}><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="mt-1 text-xs text-gray-500">
                  {d.until ? (expirada ? t('trafficManager.directives.expired', 'Expirou em {{d}}', { d: f.day(d.until) }) : t('trafficManager.directives.validUntil', 'Até {{d}}', { d: f.day(d.until) })) : t('trafficManager.directives.noEnd', 'Sem data de fim')}
                  {d.campaign_ids && d.campaign_ids.length > 0 && ` · ${d.campaign_ids.map((id) => (campaigns.find((c) => c.id === id) || { nome: id }).nome).join(', ')}`}
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
};

export default ChatTab;
